import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { PriceRequestLine } from 'models/price-request';
import { formatSignedCurrency, formatSignedPercent } from './price-request-format';

/** DD-3697: the product cell of a request line — number, name and "not offered today". */
export const ProductCell: FC<{ line: PriceRequestLine }> = ({ line }) => {
    const { t: translate } = useTranslation('price-requests');
    return (
        // Wide enough that a product name does not break word by word beside the chat column.
        <Box sx={{ minWidth: 160 }}>
            <Typography variant="body2" fontWeight={600}>
                {[line.actionNumber, line.actionName].filter(Boolean).join(' ') || line.action}
            </Typography>
            {!line.currentEnabled ? (
                <Typography variant="caption" color="text.secondary">
                    {translate('CURRENTLY_WITHDRAWN')}
                </Typography>
            ) : undefined}
        </Box>
    );
};

/** The change in currency with the percentage under it; a dash when there is none. */
/** The change in currency; "–" when there is none. Its own column, beside DeltaPercentCell. */
export const DeltaCell: FC<{ delta?: number | null; currency: string }> = ({ delta, currency }) =>
    delta === null || delta === undefined ? (
        <>–</>
    ) : (
        <Typography variant="body2" component="span" data-test="price-request.delta">
            {formatSignedCurrency(delta, currency)}
        </Typography>
    );

/** The change as a percentage of today's price, in its own column. */
export const DeltaPercentCell: FC<{ delta?: number | null; percent?: number | null }> = ({ delta, percent }) =>
    delta === null || delta === undefined || percent === null || percent === undefined ? (
        <>–</>
    ) : (
        <Typography variant="body2" component="span" data-test="price-request.delta-percent">
            {formatSignedPercent(percent)}
        </Typography>
    );

export const HistoryButton: FC<{ line: PriceRequestLine; onOpen: (line: PriceRequestLine) => void }> = ({
    line,
    onOpen,
}) => {
    const { t: translate } = useTranslation('price-requests');
    const label = translate('PRICE_HISTORY');
    return (
        <Tooltip title={label}>
            <IconButton
                size="small"
                aria-label={label}
                onClick={() => onOpen(line)}
                data-test={`price-request.history-${line.action}`}
            >
                <HistoryIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );
};
